import { useMemo, useRef, useEffect } from "react"
import { useFrame } from "@react-three/fiber"
import * as THREE from "three"
import { CM_TO_M } from "@/lib/utils"

const FRAME_WIDTH_CM = 3

const SPRITE_SCREEN_SIZE = 0.0975

function TextSprite({ text, position }: { text: string; position: [number, number, number] }) {
  const spriteRef = useRef<THREE.Sprite>(null)
  const worldPos = useMemo(() => new THREE.Vector3(...position), [position])
  const prevCamPos = useRef<THREE.Vector3>(new THREE.Vector3())

  useFrame(({ camera }) => {
    if (!spriteRef.current) return
    if (camera.position.distanceToSquared(prevCamPos.current) < 0.0001) return
    prevCamPos.current.copy(camera.position)
    const dist = camera.position.distanceTo(worldPos)
    const scale = dist * SPRITE_SCREEN_SIZE
    spriteRef.current.scale.set(scale, scale * 0.25, 1)
  })

  const texture = useMemo(() => {
    const canvas = document.createElement("canvas")
    canvas.width = 512
    canvas.height = 128
    const ctx = canvas.getContext("2d")!
    ctx.clearRect(0, 0, 512, 128)

    const padding = 20
    ctx.font = "bold 56px system-ui, sans-serif"
    const textWidth = ctx.measureText(text).width
    const bgW = textWidth + padding * 2
    const bgH = 88

    const bx = (512 - bgW) / 2
    const by = (128 - bgH) / 2

    ctx.fillStyle = "#041e7e"
    ctx.beginPath()
    ctx.roundRect(bx, by, bgW, bgH, 10)
    ctx.fill()

    ctx.fillStyle = "#ffffff"
    ctx.textAlign = "center"
    ctx.textBaseline = "middle"
    ctx.fillText(text, 256, 64)

    const tex = new THREE.CanvasTexture(canvas)
    tex.needsUpdate = true
    return tex
  }, [text])

  const material = useMemo(
    () =>
      new THREE.SpriteMaterial({
        map: texture,
        depthTest: false,
        transparent: true,
        sizeAttenuation: true,
      }),
    [texture]
  )

  useEffect(() => {
    return () => {
      texture.dispose()
    }
  }, [texture])

  useEffect(() => {
    return () => {
      material.dispose()
    }
  }, [material])

  return <sprite ref={spriteRef} position={position} material={material} />
}

interface DimensionLineProps {
  start: [number, number, number]
  end: [number, number, number]
  label: string
  offset: [number, number, number]
  axis: "x" | "y" | "z"
  tickAxis: "x" | "y" | "z"
}

function DimensionLine({ start, end, label, offset, axis, tickAxis }: DimensionLineProps) {
  const color = "#1d6fcc"
  const lineWidth = 0.008
  const tickSize = 0.04

  const sx = start[0] + offset[0]
  const sy = start[1] + offset[1]
  const sz = start[2] + offset[2]
  const ex = end[0] + offset[0]
  const ey = end[1] + offset[1]
  const ez = end[2] + offset[2]

  const length = Math.sqrt(
    Math.pow(ex - sx, 2) + Math.pow(ey - sy, 2) + Math.pow(ez - sz, 2)
  )
  const centerX = (sx + ex) / 2
  const centerY = (sy + ey) / 2
  const centerZ = (sz + ez) / 2

  const midPoint: [number, number, number] = [centerX, centerY, centerZ]

  const tickGeom: [number, number, number] =
    tickAxis === "x"
      ? [tickSize, lineWidth, lineWidth]
      : tickAxis === "y"
      ? [lineWidth, tickSize, lineWidth]
      : [lineWidth, lineWidth, tickSize]

  return (
    <group>
      {axis === "x" && (
        <mesh position={[centerX, centerY, centerZ]}>
          <boxGeometry args={[length, lineWidth, lineWidth]} />
          <meshBasicMaterial color={color} />
        </mesh>
      )}
      {axis === "y" && (
        <mesh position={[centerX, centerY, centerZ]}>
          <boxGeometry args={[lineWidth, length, lineWidth]} />
          <meshBasicMaterial color={color} />
        </mesh>
      )}
      {axis === "z" && (
        <mesh position={[centerX, centerY, centerZ]}>
          <boxGeometry args={[lineWidth, lineWidth, length]} />
          <meshBasicMaterial color={color} />
        </mesh>
      )}

      <mesh position={[sx, sy, sz]}>
        <boxGeometry args={tickGeom} />
        <meshBasicMaterial color={color} />
      </mesh>

      <mesh position={[ex, ey, ez]}>
        <boxGeometry args={tickGeom} />
        <meshBasicMaterial color={color} />
      </mesh>

      <TextSprite text={label} position={midPoint} />
    </group>
  )
}

interface DimensionLinesProps {
  visible: boolean
  height: number
  width: number
  depth: number
  sectionCount: number
  sides: "single" | "double"
}

export function DimensionLines({ visible, height, width, depth, sectionCount }: DimensionLinesProps) {
  if (!visible) return null

  const totalLengthM = (sectionCount * (FRAME_WIDTH_CM + width) + FRAME_WIDTH_CM) * CM_TO_M
  const heightM = height * CM_TO_M
  const depthM = depth * CM_TO_M

  const offsetGap = 0.12

  const heightStart: [number, number, number] = [0, 0, -depthM]
  const heightEnd: [number, number, number] = [0, heightM, -depthM]
  const heightOffset: [number, number, number] = [-offsetGap, 0, 0]
  const heightMm = height * 10

  const lengthStart: [number, number, number] = [0, 0, 0]
  const lengthEnd: [number, number, number] = [totalLengthM, 0, 0]
  const lengthOffset: [number, number, number] = [0, 0, offsetGap]
  const lengthMm = Math.round(totalLengthM * 1000)

  const depthStart: [number, number, number] = [0, 0, 0]
  const depthEnd: [number, number, number] = [0, 0, -depthM]
  const depthOffset: [number, number, number] = [-offsetGap, 0, 0]
  const depthMm = depth * 10

  return (
    <group>
      <DimensionLine
        start={heightStart}
        end={heightEnd}
        label={`${heightMm} mm`}
        offset={heightOffset}
        axis="y"
        tickAxis="x"
      />
      <DimensionLine
        start={lengthStart}
        end={lengthEnd}
        label={`${lengthMm} mm`}
        offset={lengthOffset}
        axis="x"
        tickAxis="z"
      />
      <DimensionLine
        start={depthStart}
        end={depthEnd}
        label={`${depthMm} mm`}
        offset={depthOffset}
        axis="z"
        tickAxis="x"
      />
    </group>
  )
}
